import React, { useState, useEffect, useRef } from 'react';
import './VisitModal.css';
import { api } from '../utils/api';
import { useGoogleMaps } from '../utils/GoogleMapsLoader';
import { useVisitMediaUpload, MAX_ITEMS_PER_VISIT, computeStatusAnnouncement } from '../utils/useVisitMediaUpload';

// ── Small inline icons (mobile add-media buttons) ──────────────────────────
// Plain inline SVG rather than pulling in a new icon library dependency for
// three icons.

function CameraIcon() {
    return (
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
            <circle cx="12" cy="13" r="4" />
        </svg>
    );
}

function LibraryIcon() {
    return (
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="3" width="18" height="18" rx="2" />
            <circle cx="8.5" cy="8.5" r="1.5" />
            <path d="M21 15l-5-5L5 21" />
        </svg>
    );
}

function MicIcon() {
    return (
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
            <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
            <line x1="12" y1="19" x2="12" y2="23" />
            <line x1="8" y1="23" x2="16" y2="23" />
        </svg>
    );
}

function VideoIcon() {
    return (
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polygon points="23 7 16 12 23 17 23 7" />
            <rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
        </svg>
    );
}

// Animated waveform — shown in place of the mic icon while audio is
// actively playing (see MediaTile). Ported from BT's MediaLightbox
// waveform, scaled down and re-colored for the small square tile: fewer
// bars, sized to fit here rather than a full-screen lightbox, and green
// (matching the mic icon) instead of white-on-dark. Purely decorative —
// bars don't correspond to actual audio levels. Only ever mounted while
// playing, so unlike BT's version there's no paused/frozen state to
// handle — the mic icon takes over entirely when not playing.
const TILE_WAVEFORM_BARS = [
    { h: 10, dur: 0.90, delay: 0.00 },
    { h: 19, dur: 0.75, delay: 0.10 },
    { h: 13, dur: 1.10, delay: 0.05 },
    { h: 26, dur: 0.80, delay: 0.15 },
    { h: 16, dur: 0.65, delay: 0.20 },
    { h: 22, dur: 0.90, delay: 0.08 },
    { h: 11, dur: 0.70, delay: 0.25 },
    { h: 19, dur: 1.00, delay: 0.12 },
];

function AnimatedWaveform() {
    // viewBox: 8 bars x 7px each (4 wide + 3 gap) + 2px left padding = 58.
    // Baseline at y=28; bars extend upward from there.
    return (
        <svg width="40" height="32" viewBox="0 0 58 32" fill="none" aria-hidden="true">
            {TILE_WAVEFORM_BARS.map((bar, i) => (
                <rect
                    key={i}
                    x={2 + i * 7}
                    y={28 - bar.h}
                    width={4}
                    height={bar.h}
                    rx={2}
                    fill="#43a047"
                    style={{
                        transformOrigin: `${2 + i * 7 + 2}px 28px`,
                        animationName: 'jdTileWaveformPulse',
                        animationDuration: `${bar.dur}s`,
                        animationDelay: `${bar.delay}s`,
                        animationTimingFunction: 'ease-in-out',
                        animationIterationCount: 'infinite',
                    }}
                />
            ))}
        </svg>
    );
}

// Converts Google's address_components array (present on both Autocomplete
// and Place Details results) into clean city/state/country fields. Pure
// function, no API calls — just parsing a response we already have.
function parseAddressComponents(components) {
    if (!components) return { city: null, state: null, country: null };

    const findType = (type) => components.find(c => c.types.includes(type));

    const city = findType('locality') || findType('postal_town') || findType('sublocality');
    const state = findType('administrative_area_level_1');
    const country = findType('country');

    return {
        city: city ? city.long_name : null,
        state: state ? state.short_name : null, // e.g. "CA", not "California"
        country: country ? country.long_name : null
    };
}

function formatRecordingTime(totalSeconds) {
    const m = Math.floor(totalSeconds / 60);
    const s = totalSeconds % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
}

// Hard cap on the Thoughts field (design decision, 2026-08-09). Enforced
// here via maxLength for UX, and independently by FinalizeVisit
// server-side — this constant mirrors that server-side limit, it isn't
// the actual enforcement.
const THOUGHTS_MAX_LENGTH = 1500;
const THOUGHTS_COUNTER_THRESHOLD = 300; // show the counter once this close to the limit

// Weak-connection nudge (design decision, 2026-08-09): shows a tip when
// any video item exceeds this size, based on real field data from Chabot
// Park (58-60MB videos took ~10 minutes on 1-bar 5G) vs. Walden (videos
// well under this threshold uploaded in under 15 seconds on 2 bars).
// Video only, per Jeff's call — audio is a low-probability case not worth
// the added message complexity.
const LARGE_VIDEO_THRESHOLD_BYTES = 25 * 1024 * 1024; // 25MB

// ── Media tile ───────────────────────────────────────────────────────────
// One tile per item from useVisitMediaUpload. The underlying photo/video
// preview (or, for audio, an icon) stays visible and dimmed throughout —
// only the bottom status bar changes as the item progresses. X is always
// present in the top-right corner regardless of state.

// Pauses every other playing video/audio tile in the grid when one starts —
// scoped to .media-tile-grid so this never reaches outside the media zone.
function pauseOtherMediaTiles(playingEl) {
    document.querySelectorAll('.media-tile-grid video, .media-tile-grid audio').forEach(el => {
        if (el !== playingEl) el.pause();
    });
}

// 1E.4 (ChatGPT feedback item 10): tile space is tight, so each cap
// violation gets a short, specific label rather than the full errorMessage
// text. Keyed by AddVisitMedia's/useVisitMediaUpload's `reason` codes;
// the fallback covers a validation reason the frontend doesn't have a
// specific label for yet (defensive — should rarely if ever be hit).
const VALIDATION_FAILURE_LABELS = {
    file_too_large: 'Upload Failed (File Too Large)',
    too_many_items: 'Upload Failed (Too Many Items)',
    visit_too_large: 'Upload Failed (Visit Too Large)',
};
const DEFAULT_VALIDATION_FAILURE_LABEL = 'Upload Failed (Not Allowed)';

function MediaTile({ item, onRemove, onRetry }) {
    const { type, status, previewUrl, thumbnailUrl, uploadProgress, retryDisabled, staleFile, validationFailed, validationReason, faultyFile } = item;
    // 1E.3 polish: floor at 8% so the pie never renders as a bare, easy-
    // to-miss full circle at the very start. Switches to a solid fill
    // (not the conic-gradient) once done, to avoid a rendering seam
    // artifact right at the 0%/100% boundary. No indeterminate spin — a
    // 1-part item (uploadProgress.total <= 1) just sits at the 8% floor
    // throughout its (typically brief) upload, then jumps straight to
    // solid, same final step as a real multi-part item.
    const uploadTotal = uploadProgress?.total || 1;
    const uploadDone = uploadProgress?.done || 0;
    const uploadPct = Math.max(8, Math.min(100, Math.round((uploadDone / uploadTotal) * 100)));
    const uploadHasRealProgress = Boolean(uploadProgress && uploadProgress.total > 1);
    const dimmed = status !== 'complete';
    const mediaElRef = useRef(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [progress, setProgress] = useState(0); // 0 to 1

    const playable = status === 'complete' && (type === 'video' || type === 'audio');

    useEffect(() => {
        const el = mediaElRef.current;
        if (!el) return;

        const onPlay = () => setIsPlaying(true);
        const onPause = () => setIsPlaying(false);
        const onTimeUpdate = () => {
            if (el.duration) setProgress(el.currentTime / el.duration);
        };
        const onEnded = () => {
            setIsPlaying(false);
            setProgress(0);
        };

        el.addEventListener('play', onPlay);
        el.addEventListener('pause', onPause);
        el.addEventListener('timeupdate', onTimeUpdate);
        el.addEventListener('ended', onEnded);

        return () => {
            el.removeEventListener('play', onPlay);
            el.removeEventListener('pause', onPause);
            el.removeEventListener('timeupdate', onTimeUpdate);
            el.removeEventListener('ended', onEnded);
        };
    }, [playable]);

    const togglePlay = () => {
        const el = mediaElRef.current;
        if (!el) return;
        if (el.paused) {
            pauseOtherMediaTiles(el);
            el.play();
        } else {
            el.pause();
        }
    };

    const handleSeek = (e) => {
        e.stopPropagation();
        const el = mediaElRef.current;
        if (!el || !el.duration) return;
        const rect = e.currentTarget.getBoundingClientRect();
        const ratio = (e.clientX - rect.left) / rect.width;
        el.currentTime = ratio * el.duration;
    };

    return (
        <div className="media-tile">
            {/* Inner wrapper owns the rounded-corner clipping. X lives
                outside it (as a sibling) so it can sit above/outside the
                tile's edge without getting clipped. */}
            <div className="media-tile-inner">
                <div
                    className={`media-tile-media ${playable ? 'playable' : ''}`}
                    onClick={playable ? togglePlay : undefined}
                >
                    {type === 'photo' && previewUrl && (
                        <img src={previewUrl} alt="" className={dimmed ? 'dimmed' : ''} />
                    )}
                    {/* Video: no client-side thumbnail attempt — per BT's
                        approach (and general best practice), a real
                        thumbnail is a server-side MediaConvert Frame Capture
                        output, not something generated from a local Blob
                        URL. Shows a generic icon until status is Complete,
                        then renders real, playable video content. */}
                    {type === 'video' && status !== 'complete' && (
                        <div className="media-tile-video-icon">
                            <VideoIcon />
                        </div>
                    )}
                    {type === 'video' && status === 'complete' && previewUrl && (
                        <video
                            ref={mediaElRef}
                            src={previewUrl}
                            poster={thumbnailUrl || undefined}
                            playsInline
                        />
                    )}
                    {type === 'audio' && (
                        <div className={`media-tile-audio-icon ${dimmed ? 'dimmed' : ''}`}>
                            {isPlaying ? <AnimatedWaveform /> : <MicIcon />}
                        </div>
                    )}
                    {type === 'audio' && status === 'complete' && (
                        <audio ref={mediaElRef} src={previewUrl} />
                    )}
                </div>

                {playable && (
                    <div className="media-tile-playback-bar">
                        <button
                            type="button"
                            className="media-tile-play-button"
                            onClick={(e) => { e.stopPropagation(); togglePlay(); }}
                            aria-label={isPlaying ? 'Pause' : 'Play'}
                        >
                            {isPlaying ? '\u23F8' : '\u25B6'}
                        </button>
                        <div className="media-tile-progress-track" onClick={handleSeek}>
                            <div className="media-tile-progress-fill" style={{ width: `${progress * 100}%` }} />
                        </div>
                    </div>
                )}

                {status === 'uploading' && (
                    <div className="media-tile-bar uploading">
                        <span>Uploading</span>
                        <span
                            className="media-tile-upload-pie"
                            style={uploadPct >= 100 ? { background: '#333' } : { '--pct': uploadPct }}
                            aria-hidden="true"
                        />
                        <span className="sr-only">
                            {uploadHasRealProgress ? `${uploadPct}% uploaded` : 'Uploading, please wait'}
                        </span>
                    </div>
                )}
                {status === 'processing' && (
                    <div className="media-tile-bar processing">Processing&hellip;</div>
                )}
                {status === 'failed' && (
                    <div className="media-tile-failed-overlay">
                        <span className="media-tile-failed-label">
                            {staleFile
                                ? 'Upload Failed (Stale File)'
                                : faultyFile
                                ? 'Upload Failed (Faulty File)'
                                : validationFailed
                                ? (VALIDATION_FAILURE_LABELS[validationReason] || DEFAULT_VALIDATION_FAILURE_LABEL)
                                : 'Upload Failed'}
                        </span>
                        {!retryDisabled && (
                            <button type="button" className="media-tile-retry-button" onClick={onRetry}>
                                Retry
                            </button>
                        )}
                    </div>
                )}
            </div>

            <button
                type="button"
                className="media-tile-remove"
                onClick={onRemove}
                aria-label="Remove"
            >
                {'\u00D7'}
            </button>
        </div>
    );
}

const VisitModal = ({ isOpen, onClose, discId }) => {
    const [locationMethod, setLocationMethod] = useState(null);
    const [location, setLocation] = useState(null);
    const [courseName, setCourseName] = useState('');
    const [nearbyPlaces, setNearbyPlaces] = useState([]);
    const [locationLoading, setLocationLoading] = useState(false);
    const [locationError, setLocationError] = useState(null);
    const [thoughts, setThoughts] = useState('');
    const [familyOnly, setFamilyOnly] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [submitSuccess, setSubmitSuccess] = useState(false);
    const [submitError, setSubmitError] = useState(null);

    // Draft lifecycle (Recipe Principle 2)
    const [visitId, setVisitId] = useState(null);
    const [draftError, setDraftError] = useState(null);
    const visitIdRef = useRef(null);
    const submitSuccessRef = useRef(false);
    // Guards against openDraft firing more than once per modal open — seen
    // in testing as 2-3 CreateDraftVisit invocations within the same
    // second from what should be a single tap. Root cause not yet
    // isolated (candidates: mobile ghost-click/duplicate touch events, or
    // the component genuinely remounting) — this closes the symptom
    // regardless of which it turns out to be.
    const draftRequestInFlightRef = useRef(false);
    useEffect(() => { visitIdRef.current = visitId; }, [visitId]);
    useEffect(() => { submitSuccessRef.current = submitSuccess; }, [submitSuccess]);

    // Audio recording (new — not ported from anywhere; BT's reference files
    // covered upload/playback, not the recording interaction itself)
    const [isRecording, setIsRecording] = useState(false);
    const [recordingSeconds, setRecordingSeconds] = useState(0);
    const [recordingError, setRecordingError] = useState(null);
    const mediaRecorderRef = useRef(null);
    const recordedChunksRef = useRef([]);
    const recordingStreamRef = useRef(null);
    const recordingTimerRef = useRef(null);

    const courseInputRef = useRef(null);
    const autocompleteRef = useRef(null);
    const desktopMediaInputRef = useRef(null);
    const mobileLibraryInputRef = useRef(null);
    const mobileCameraInputRef = useRef(null);
    const { isLoaded } = useGoogleMaps();

    const mediaUpload = useVisitMediaUpload(visitId);

    // ── Accessibility: live status announcements ─────────────────────────
    //
    // One shared aria-live region for the whole media section, rather than
    // one per tile — avoids duplicate/competing announcements when several
    // tiles change at once (e.g. a multi-select batch). previousMediaStatusesRef
    // holds each item's status+type as of the LAST render, so each new
    // render can diff against it and announce only what actually changed.
    const [mediaAnnouncement, setMediaAnnouncement] = useState('');
    const previousMediaStatusesRef = useRef({});

    useEffect(() => {
        const message = computeStatusAnnouncement(previousMediaStatusesRef.current, mediaUpload.items);
        if (message) {
            setMediaAnnouncement(message);
        }

        const nextSnapshot = {};
        mediaUpload.items.forEach(item => {
            nextSnapshot[item.clientId] = { status: item.status, type: item.type };
        });
        previousMediaStatusesRef.current = nextSnapshot;
    }, [mediaUpload.items]);

    // ── Whole-draft delete (Cancel/Close) ────────────────────────────────
    //
    // 1E.4 (ChatGPT feedback item 7): background retry matching the
    // X-button's per-item pattern (attemptRemovalDelete in
    // useVisitMediaUpload.js) — same delays, same "never surface a failure
    // to the person" reasoning. Unlike that per-item case, there is no
    // Submit-time fallback here (nothing is being submitted when the
    // person cancels), so once retries are exhausted, the draft's own TTL
    // (48h, asynchronous — DynamoDB does not guarantee prompt deletion at
    // the exact expiry timestamp) and the S3 lifecycle rules are the
    // eventual backstop, not something this code arranges itself.
    const DRAFT_DELETE_RETRY_DELAYS_MS = [2000, 5000, 15000];
    const draftDeleteRetryTimerRef = useRef(null);

    const attemptDraftDelete = (visitIdToDelete, attempt = 0) => {
        api.deleteDraftVisit(visitIdToDelete)
            .then(() => {
                draftDeleteRetryTimerRef.current = null;
            })
            .catch(error => {
                console.error(`Error deleting draft visit ${visitIdToDelete} (attempt ${attempt + 1}):`, error);
                if (attempt < DRAFT_DELETE_RETRY_DELAYS_MS.length) {
                    draftDeleteRetryTimerRef.current = setTimeout(
                        () => attemptDraftDelete(visitIdToDelete, attempt + 1),
                        DRAFT_DELETE_RETRY_DELAYS_MS[attempt]
                    );
                } else {
                    // Retries exhausted, silently — Cancel/Close already
                    // completed from the person's perspective, and nothing
                    // here should surface a failure after the fact. TTL +
                    // the S3 lifecycle rules are the eventual fallback.
                    draftDeleteRetryTimerRef.current = null;
                }
            });
    };

    // Belt-and-suspenders: if the component genuinely unmounts (not just
    // isOpen toggling — see the note on reset() elsewhere in this file)
    // while a retry is pending, stop scheduling further attempts against
    // an unmounted context.
    useEffect(() => {
        return () => {
            if (draftDeleteRetryTimerRef.current) {
                clearTimeout(draftDeleteRetryTimerRef.current);
            }
        };
    }, []);

    // ── Open / close lifecycle ───────────────────────────────────────────

    useEffect(() => {
        if (isOpen) {
            openDraft();
        } else {
            // Closing (Cancel, X, or auto-close after a successful submit).
            mediaUpload.reset();
            cancelRecording();
            previousMediaStatusesRef.current = {};
            setMediaAnnouncement('');

            // Don't delete a draft we just successfully submitted — only
            // clean up an abandoned one.
            if (visitIdRef.current && !submitSuccessRef.current) {
                attemptDraftDelete(visitIdRef.current);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);

    useEffect(() => {
        // Auto-focus text input when Type In is selected
        if (locationMethod === 'type' && courseInputRef.current) {
            courseInputRef.current.focus();
        }
    }, [locationMethod]);

    const resetForm = () => {
        setVisitId(null);
        setDraftError(null);
        setLocationMethod(null);
        setLocation(null);
        setCourseName('');
        setNearbyPlaces([]);
        setLocationError(null);
        setThoughts('');
        setFamilyOnly(false);
        setSubmitSuccess(false);
        setSubmitting(false);
        setSubmitError(null);
        setRecordingError(null);
    };

    const openDraft = async () => {
        if (draftRequestInFlightRef.current) {
            console.warn('openDraft called while a request was already in flight — skipping duplicate');
            return;
        }
        draftRequestInFlightRef.current = true;

        resetForm();
        try {
            const { visitId: newVisitId } = await api.createDraftVisit(discId);
            setVisitId(newVisitId);
        } catch (error) {
            console.error('Error creating draft visit:', error);
            setDraftError("We couldn't get things ready. Please close and try again.");
        } finally {
            draftRequestInFlightRef.current = false;
        }
    };

    // ── Location (unchanged from before) ─────────────────────────────────

    const handleShareLocation = () => {
        if (locationMethod === 'share') return;

        setLocationMethod('share');
        setLocationLoading(true);
        setLocationError(null);
        setCourseName('');
        setLocation(null);
        setNearbyPlaces([]);

        if (!navigator.geolocation) {
            setLocationError('Geolocation is not supported by your browser');
            setLocationLoading(false);
            setLocationMethod('type');
            return;
        }

        navigator.geolocation.getCurrentPosition(
            async (position) => {
                const coords = {
                    lat: position.coords.latitude,
                    lon: position.coords.longitude
                };
                setLocation(coords);

                await searchNearbyPlaces(coords);

                setLocationLoading(false);
            },
            (error) => {
                console.log('Geolocation error:', error);
                setLocationError('Location access denied.');
                setLocationLoading(false);
                setLocationMethod('type');
            },
            {
                enableHighAccuracy: true,
                timeout: 10000,
                maximumAge: 0
            }
        );
    };

    const searchNearbyPlaces = async (coords) => {
        if (!isLoaded || !window.google) {
            setLocationError('Maps not loaded yet. Please try again.');
            return;
        }

        try {
            const service = new window.google.maps.places.PlacesService(
                document.createElement('div')
            );

            const request = {
                location: new window.google.maps.LatLng(coords.lat, coords.lon),
                radius: 8046.72, // 5 miles in meters
                keyword: 'disc golf course',
                type: 'establishment'
            };

            service.nearbySearch(request, (results, status) => {
                if (status === window.google.maps.places.PlacesServiceStatus.OK && results) {
                    const courses = results.slice(0, 5).map((place, index) => ({
                        id: place.place_id || index,
                        name: place.name,
                        lat: place.geometry.location.lat(),
                        lon: place.geometry.location.lng(),
                        vicinity: place.vicinity
                    }));

                    setNearbyPlaces(courses);

                    if (courses.length === 0) {
                        setLocationError('No disc golf courses found nearby. Please use Type In.');
                        setLocationMethod('type');
                    }
                } else {
                    console.log('Places search failed:', status);
                    setLocationError('No disc golf courses found nearby. Please use Type In.');
                    setLocationMethod('type');
                    setNearbyPlaces([]);
                }
            });
        } catch (error) {
            console.error('Error searching places:', error);
            setLocationError('Failed to search nearby. Please use Type In.');
            setLocationMethod('type');
        }
    };

    const handleTypeIn = () => {
        setLocationMethod('type');
        setLocation(null);
        setNearbyPlaces([]);
        setLocationError(null);
        setCourseName('');
    };

    useEffect(() => {
        if (locationMethod === 'type' && isLoaded && courseInputRef.current && window.google) {
            const autocomplete = new window.google.maps.places.Autocomplete(
                courseInputRef.current,
                {
                    types: ['establishment'],
                    fields: ['name', 'geometry', 'place_id', 'formatted_address', 'address_components']
                }
            );

            autocomplete.setComponentRestrictions({ country: ['us', 'ca'] });

            autocomplete.addListener('place_changed', () => {
                const place = autocomplete.getPlace();

                if (place.geometry && place.geometry.location) {
                    const { city, state, country } = parseAddressComponents(place.address_components);
                    const coords = {
                        lat: place.geometry.location.lat(),
                        lon: place.geometry.location.lng(),
                        city,
                        state,
                        country
                    };
                    setLocation(coords);
                    setCourseName(place.name || place.formatted_address);
                }
            });

            autocompleteRef.current = autocomplete;
        }
    }, [locationMethod, isLoaded]);

    // ── Media add (photo/video via file input) ───────────────────────────

    const handleMediaFilesSelected = (e) => {
        // Copy into a real array BEFORE clearing the input — e.target.files
        // is a live FileList tied to the input's current value, so resetting
        // e.target.value first (to allow re-selecting the same file later)
        // empties it out from under us before addFiles ever sees it.
        const files = Array.from(e.target.files);
        e.target.value = '';
        if (files.length > 0) {
            mediaUpload.addFiles(files);
        }
    };

    // ── Audio recording ───────────────────────────────────────────────────

    const startRecording = async () => {
        setRecordingError(null);
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            recordingStreamRef.current = stream;
            recordedChunksRef.current = [];

            const recorder = new MediaRecorder(stream);
            mediaRecorderRef.current = recorder;

            recorder.ondataavailable = (e) => {
                if (e.data.size > 0) recordedChunksRef.current.push(e.data);
            };

            recorder.onstop = () => {
                stream.getTracks().forEach(track => track.stop());
                recordingStreamRef.current = null;

                // recorder.mimeType often comes back with a codec parameter
                // attached (e.g. "audio/webm;codecs=opus" in Chrome) — strip
                // it so both the extension we derive and the contentType we
                // send to AddVisitMedia are the clean base type it expects.
                const rawMimeType = recorder.mimeType || 'audio/webm';
                const mimeType = rawMimeType.split(';')[0].trim();
                const blob = new Blob(recordedChunksRef.current, { type: mimeType });
                recordedChunksRef.current = [];

                if (blob.size > 0) {
                    const extension = mimeType.includes('mp4') ? 'm4a' : 'webm';
                    const file = new File([blob], `recording-${Date.now()}.${extension}`, { type: mimeType });
                    mediaUpload.addFiles([file]);
                }
            };

            // A short settle delay before starting capture — on many
            // browsers/OSes, the mic's auto-gain-control and echo
            // cancellation haven't stabilized in the first moment after
            // permission is granted, and starting the recorder immediately
            // can lose or mute the first second or so of real audio.
            await new Promise(resolve => setTimeout(resolve, 300));

            recorder.start();
            setIsRecording(true);
            setRecordingSeconds(0);
            recordingTimerRef.current = setInterval(() => {
                setRecordingSeconds(s => s + 1);
            }, 1000);
        } catch (error) {
            console.error('Error starting audio recording:', error);
            setRecordingError("We couldn't access your microphone. Please check your browser's permission settings.");
        }
    };

    // Stops and uploads what was recorded (the normal "Stop" button path).
    const stopRecording = () => {
        if (recordingTimerRef.current) {
            clearInterval(recordingTimerRef.current);
            recordingTimerRef.current = null;
        }
        if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
            mediaRecorderRef.current.stop(); // triggers onstop -> uploads via mediaUpload.addFiles
        } else if (recordingStreamRef.current) {
            recordingStreamRef.current.getTracks().forEach(track => track.stop());
            recordingStreamRef.current = null;
        }
        setIsRecording(false);
    };

    // Discards an in-progress recording without uploading — used when the
    // modal closes mid-recording.
    const cancelRecording = () => {
        if (recordingTimerRef.current) {
            clearInterval(recordingTimerRef.current);
            recordingTimerRef.current = null;
        }
        if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
            mediaRecorderRef.current.onstop = null; // detach so stopping here doesn't trigger an upload
            mediaRecorderRef.current.stop();
        }
        if (recordingStreamRef.current) {
            recordingStreamRef.current.getTracks().forEach(track => track.stop());
            recordingStreamRef.current = null;
        }
        setIsRecording(false);
    };

    // ── Submit ────────────────────────────────────────────────────────────

    // 1E.4: only items that hold (or held) a real reservation count toward
    // the item cap — a failed tile (wrong type, oversized, over-cap) never
    // became one and shouldn't count against the limit it was itself
    // rejected by.
    const atItemCap = mediaUpload.items.filter(item => item.status !== 'failed').length >= MAX_ITEMS_PER_VISIT;

    // Scoped to 'uploading' specifically (not 'processing' or 'complete')
    // — the message is about upload time, so it should clear the moment
    // the upload itself finishes, even if transcoding is still ongoing.
    const hasLargeVideo = mediaUpload.items.some(
        item => item.type === 'video' && item.status === 'uploading' && (item.file?.size || 0) > LARGE_VIDEO_THRESHOLD_BYTES
    );

    const blockingMessage = mediaUpload.items.some(item => item.status === 'failed')
        ? 'One or more uploads failed — remove them or tap Retry before submitting.'
        : mediaUpload.items.some(item => item.status === 'uploading')
        ? 'Please wait until all files are uploaded.'
        : null;

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!courseName) {
            alert('Please select or enter a course name');
            return;
        }
        if (!visitId || mediaUpload.hasBlockingItems) {
            return;
        }

        setSubmitting(true);
        setSubmitError(null);

        try {
            await api.finalizeVisit(visitId, {
                location,
                courseName,
                thoughts: thoughts.trim() || null,
                familyOnly,
                removedMediaIds: mediaUpload.getPendingRemovalMediaIds()
            });

            setSubmitting(false);
            setSubmitSuccess(true);

            setTimeout(() => {
                onClose();
            }, 5000);
        } catch (error) {
            console.error('Error finalizing visit:', error);
            setSubmitting(false);
            if (error.status === 404) {
                alert('Your visit has expired — please close it and try again.');
            } else if (error.reason === 'thoughts_too_long') {
                setSubmitError(`Your thoughts are too long — please shorten to ${THOUGHTS_MAX_LENGTH} characters.`);
            } else {
                alert('Failed to submit visit. Please try again.');
            }
        }
    };

    if (!isOpen) return null;

    if (!isLoaded) {
        return (
            <div className="modal-overlay" onClick={onClose}>
                <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                    <div className="modal-header">
                        <h2>Update Disc Location & Share Your Thoughts</h2>
                        <button className="close-button" onClick={onClose}>&times;</button>
                    </div>
                    <div className="visit-form">
                        <p style={{textAlign: 'center', padding: '40px'}}>Loading maps...</p>
                    </div>
                </div>
            </div>
        );
    }

    // Closing is always safe now — every upload is individually abortable,
    // so there's no need to force the person to wait for anything except
    // the brief Finalize network call itself.
    const canClose = !submitting;

    return (
        <div className="modal-overlay" onClick={canClose ? onClose : undefined}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                    <h2>Update Disc Location & Share Your Thoughts</h2>
                    <button className="close-button" onClick={onClose} disabled={!canClose}>&times;</button>
                </div>

                {submitSuccess ? (
                    <div className="success-screen">
                        <div className="success-icon">✓</div>
                        <h2>Thank you so much!</h2>
                        <p>The family reviews all submissions&mdash;we'll post it soon!</p>
                        <button className="done-button" onClick={onClose}>Done</button>
                        <p className="auto-close-text">This message will close in a few seconds</p>
                    </div>
                ) : draftError ? (
                    <div className="error-screen">
                        <h2>Something Went Wrong</h2>
                        <p>This usually means a slow or interrupted connection, though it could be a temporary problem on our end. Try again below, or close and come back in a few minutes.</p>
                        <button className="done-button" onClick={openDraft}>Try Again</button>
                        <button type="button" className="cancel-link" onClick={onClose}>Close</button>
                    </div>
                ) : (

                    <form onSubmit={handleSubmit} className="visit-form">
                        {/* Single shared live region for all media-tile
                            status changes and removals — see the effect
                            above that populates mediaAnnouncement. */}
                        <div className="sr-only" role="status" aria-live="polite" aria-atomic="true">
                            {mediaAnnouncement}
                        </div>

                        {/* Location Section — deliberately placed first per
                            Jeff's product judgment (feels like the natural
                            starting point). Note: this is an intentional
                            deviation from the DoZESQ Media Upload Recipe's
                            Principle 1 (media-first, to maximize upload time)
                            — flagged for a future Recipe note, not an
                            oversight. */}
                        <section className="form-section location-section">
                            <h3>Disc Location</h3>

                            <div className="location-buttons">
                                <button
                                    type="button"
                                    className={`location-choice-button ${locationMethod === 'share' ? 'active' : ''}`}
                                    onClick={handleShareLocation}
                                >
                                    Use Location
                                </button>
                                <button
                                    type="button"
                                    className={`location-choice-button ${locationMethod === 'type' ? 'active' : ''}`}
                                    onClick={handleTypeIn}
                                >
                                    Type In
                                </button>
                            </div>

                            {locationMethod === 'share' && (
                                <div className="location-input-area">
                                    {locationLoading ? (
                                        <p className="loading-text">Finding nearby courses...</p>
                                    ) : locationError ? (
                                        <p className="error-text">{locationError}</p>
                                    ) : nearbyPlaces.length > 0 ? (
                                        <>
                                            <div className="course-list">
                                                {nearbyPlaces.map(place => (
                                                    <button
                                                        key={place.id}
                                                        type="button"
                                                        className={`course-button ${courseName === place.name ? 'selected' : ''}`}
                                                        onClick={() => {
                                                            setCourseName(place.name);
                                                            setLocation({
                                                                lat: place.lat,
                                                                lon: place.lon
                                                            });

                                                            // Nearby Search (used to find this place) never
                                                            // returns address_components, so we make one
                                                            // additional Place Details call here — only when
                                                            // the person actually selects a course, not once
                                                            // per search result shown.
                                                            if (window.google) {
                                                                const detailsService = new window.google.maps.places.PlacesService(
                                                                    document.createElement('div')
                                                                );
                                                                detailsService.getDetails(
                                                                    { placeId: place.id, fields: ['address_components'] },
                                                                    (result, status) => {
                                                                        if (status === window.google.maps.places.PlacesServiceStatus.OK && result) {
                                                                            const { city, state, country } = parseAddressComponents(result.address_components);
                                                                            setLocation(prev => ({ ...prev, city, state, country }));
                                                                        }
                                                                    }
                                                                );
                                                            }
                                                        }}
                                                    >
                                                        <span className="course-name">{place.name}</span>
                                                        {courseName === place.name && <span className="checkmark">&#10004;</span>}
                                                    </button>
                                                ))}
                                            </div>
                                            <p className="helper-text-small">Don't see the course? Choose Type In above.</p>
                                        </>
                                    ) : null}
                                </div>
                            )}

                            {locationMethod === 'type' && (
                                <div className="location-input-area">
                                    <div className="form-group">
                                        <input
                                            ref={courseInputRef}
                                            type="text"
                                            placeholder="Start typing a disc golf course name..."
                                            required
                                        />
                                    </div>
                                </div>
                            )}
                        </section>

                        <div className="section-divider"></div>

                        {/* Media Section — groups visually with Thoughts below
                            it (no divider between them), per Jeff's call. */}
                        <section className="form-section media-section">
                            <h3>Photos, Videos &amp; Audio <span className="optional-text">(optional)</span></h3>

                            <input
                                ref={desktopMediaInputRef}
                                type="file"
                                accept="image/*,video/*"
                                multiple
                                onChange={handleMediaFilesSelected}
                                className="file-input-hidden"
                            />
                            <input
                                ref={mobileLibraryInputRef}
                                type="file"
                                accept="image/*,video/*"
                                multiple
                                onChange={handleMediaFilesSelected}
                                className="file-input-hidden"
                            />
                            <input
                                ref={mobileCameraInputRef}
                                type="file"
                                accept="image/*,video/*"
                                capture="environment"
                                onChange={handleMediaFilesSelected}
                                className="file-input-hidden"
                            />

                            {/* Desktop: two stacked buttons */}
                            <div className="media-buttons desktop-media-buttons">
                                <button
                                    type="button"
                                    className="file-upload-button"
                                    disabled={!visitId || atItemCap}
                                    onClick={() => desktopMediaInputRef.current?.click()}
                                >
                                    Upload Photos/Videos
                                </button>
                                <button
                                    type="button"
                                    className={`file-upload-button ${isRecording ? 'recording-active' : ''}`}
                                    disabled={!visitId || (atItemCap && !isRecording)}
                                    onClick={isRecording ? stopRecording : startRecording}
                                >
                                    {isRecording ? `Stop Recording (${formatRecordingTime(recordingSeconds)})` : 'Record Audio'}
                                </button>
                            </div>

                            {/* Mobile: three icons */}
                            <div className="media-buttons mobile-media-buttons">
                                <button
                                    type="button"
                                    className="media-icon-button"
                                    disabled={!visitId || atItemCap}
                                    onClick={() => mobileCameraInputRef.current?.click()}
                                >
                                    <CameraIcon />
                                    <span>Camera</span>
                                </button>
                                <button
                                    type="button"
                                    className="media-icon-button"
                                    disabled={!visitId || atItemCap}
                                    onClick={() => mobileLibraryInputRef.current?.click()}
                                >
                                    <LibraryIcon />
                                    <span>Photo Library</span>
                                </button>
                                <button
                                    type="button"
                                    className={`media-icon-button ${isRecording ? 'recording-active' : ''}`}
                                    disabled={!visitId || (atItemCap && !isRecording)}
                                    onClick={isRecording ? stopRecording : startRecording}
                                >
                                    <MicIcon />
                                    <span>{isRecording ? formatRecordingTime(recordingSeconds) : 'Audio'}</span>
                                </button>
                            </div>

                            {atItemCap && (
                                <p className="helper-text-small">
                                    You've reached the maximum of {MAX_ITEMS_PER_VISIT} items for this visit.
                                </p>
                            )}

                            {recordingError && <p className="error-text">{recordingError}</p>}

                            {mediaUpload.items.length > 0 && (
                                <div className="media-tile-grid">
                                    {mediaUpload.items.map(item => (
                                        <MediaTile
                                            key={item.clientId}
                                            item={item}
                                            onRemove={() => mediaUpload.removeItem(item.clientId)}
                                            onRetry={() => mediaUpload.retryItem(item.clientId)}
                                        />
                                    ))}
                                </div>
                            )}

                            {mediaUpload.items.some(item => item.status === 'processing') && (
                                <div className="processing-banner">
                                    Your file is processing and won't be playable yet, but you can go ahead and submit.
                                </div>
                            )}

                            {hasLargeVideo && (
                                <div className="large-video-banner">
                                    On slow connections, video uploads take time — shorter videos upload faster
                                </div>
                            )}
                        </section>

                        {/* Thoughts Section — no divider before this; groups
                            visually with Media above it, per Jeff's call. */}
                        <section className="form-section thoughts-section">
                            <h3>Your Thoughts <span className="optional-text">(optional)</span></h3>

                            <div className="form-group">
                                <textarea
                                    value={thoughts}
                                    onChange={(e) => setThoughts(e.target.value)}
                                    placeholder="Type any thoughts you'd like to share (about grief, loss, kindness, joy or anything else)"
                                    rows="4"
                                    maxLength={THOUGHTS_MAX_LENGTH}
                                />
                                {thoughts.length >= THOUGHTS_MAX_LENGTH - THOUGHTS_COUNTER_THRESHOLD && (
                                    <p className="helper-text-small">
                                        {THOUGHTS_MAX_LENGTH - thoughts.length} characters remaining
                                    </p>
                                )}
                            </div>

                            <div className="family-only-toggle">
                                <label>
                                    <input
                                        type="checkbox"
                                        checked={familyOnly}
                                        onChange={(e) => setFamilyOnly(e.target.checked)}
                                    />
                                    Please consider making my thoughts (including photos, video and audio) for Jeffrey's family only
                                </label>
                            </div>
                        </section>

                        <div className="modal-actions">
                            {submitError && (
                                <p className="helper-text-small blocking-message">{submitError}</p>
                            )}
                            {blockingMessage && (
                                <p className="helper-text-small blocking-message">{blockingMessage}</p>
                            )}
                            <button
                                type="submit"
                                className="submit-button"
                                disabled={submitting || !locationMethod || !visitId || mediaUpload.hasBlockingItems}
                            >
                                {submitting ? 'Submitting...' : 'Submit'}
                            </button>
                            <button type="button" className="cancel-link" onClick={onClose} disabled={!canClose}>
                                Cancel
                            </button>
                        </div>
                    </form>
                )}
            </div>
        </div>
    );
};

export default VisitModal;